import { useState, useEffect } from 'react';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { CreateApiKeyModal } from '../../components/features/CreateApiKeyModal';
import { ApiKey } from '../../types';
import apiClient from '../../services/api';

export function ApiKeysPage() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;

  const fetchApiKeys = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await apiClient.get<{ data: ApiKey[]; pagination: { total: number } }>(
        `/api-keys?page=${page}&page_size=${pageSize}`
      );
      setApiKeys(response.data.data);
      setTotal(response.data.pagination.total);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, [page]);

  const handleRevoke = async (id: number) => {
    if (!confirm('确定要吊销此 API Key 吗？吊销后将无法恢复。')) {
      return;
    }

    try {
      await apiClient.post(`/api-keys/${id}/revoke`);
      fetchApiKeys();
    } catch (err: any) {
      alert(err.response?.data?.error?.message || '吊销失败');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('确定要删除此 API Key 吗？')) {
      return;
    }

    try {
      await apiClient.delete(`/api-keys/${id}`);
      fetchApiKeys();
    } catch (err: any) {
      alert(err.response?.data?.error?.message || '删除失败');
    }
  };

  const handleCreateSuccess = () => {
    setIsModalOpen(false);
    fetchApiKeys();
  };

  const formatDate = (date: string | null) => {
    if (!date) return '永不过期';
    return new Date(date).toLocaleString('zh-CN');
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">API Keys</h1>
          <p className="text-sm text-gray-600 mt-1">管理您的 API 访问密钥</p>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          创建 API Key
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <Card>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : apiKeys.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            暂无 API Key，点击上方按钮创建
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">名称</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Key 前缀</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">状态</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">限流</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">过期时间</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">最后使用</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">创建时间</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">操作</th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.map((key) => (
                  <tr key={key.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm">{key.name}</td>
                    <td className="py-3 px-4 text-sm font-mono">{key.key_prefix}...</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        key.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {key.status === 'active' ? '活跃' : '已吊销'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {key.rate_limit_rpm}/min, {key.rate_limit_rps}/s
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {formatDate(key.expires_at)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {formatDate(key.last_used_at)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {formatDate(key.created_at)}
                    </td>
                    <td className="py-3 px-4 text-right space-x-2">
                      {key.status === 'active' && (
                        <button
                          onClick={() => handleRevoke(key.id)}
                          className="text-yellow-600 hover:text-yellow-800 text-sm"
                        >
                          吊销
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(key.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        删除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              共 {total} 条记录
            </div>
            <div className="flex space-x-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                上一页
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                下一页
              </Button>
            </div>
          </div>
        )}
      </Card>

      <CreateApiKeyModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
}
